<template>
  <PopupBase
    :show="isOpen && !isLoading"
    class="ccrs-contact-popup-container"
    :close-on-outside-click="true"
    size="small"
    @close-pop-up="closePopup"
  >
    <div class="ccrs-contact-popup">
      <h4>{{ i18n.tv('contactUs', 'contactForm') }}</h4>

      <h5>{{ i18n.tv('yourAssignedSalesContact', 'ccrs') }}</h5>

      <div
        v-if="salesPartnerData"
        class="ccrs-contact-popup__row"
      >
        <span>{{ contactName }}</span>
      </div>

      <div
        v-if="salesPartnerData"
        class="ccrs-contact-popup__row"
      >
        <span>
          <a
            href="javascript:void(0)"
            :title="i18n.tv('copyMail', 'ccrs')"
            @click="copyEmail"
            class="ccrs-contact-popup__copy"
          >
            <ActionCopy />
          </a>
          <a
            :href="mailtoLink"
            :title="i18n.tv('openMailProgram', 'ccrs')"
            class="ccrs-contact-popup__email"
            @click="handleMailtoClick"
          >
            <CommunicationEmail />
            {{ contactEmail }}
          </a>
        </span>
      </div>
    </div>
  </PopupBase>
</template>

<script setup lang="ts">
import {storeToRefs} from 'pinia'
import PopupBase from '~/components/popup/PopupBase.vue'
import useI18nExtended from '~/composables/usei18nExtended'
import {useUserStore} from '@/store/user'
import ActionCopy from '~/assets/icons/action-copy.svg?component'
import CommunicationEmail from '~/assets/icons/communication_email.svg?component'
import {useI18n} from 'vue-i18n'

const i18n = useI18nExtended()
const i18nVue = useI18n()
const userStore = useUserStore()
const app = useNuxtApp()
const auth = app.$auth
const config = useRuntimeConfig()

const {
  salesPartnerData,
  loadedCustomerUuid
} = storeToRefs(userStore)

const {fetchSalesPartnerData} = userStore

const props = defineProps<{
  isOpen: boolean,
  searchTerm: string,
  additionalInfo?: string,
  trackingCallback?: () => void,
  trackCopyMail?: () => void,
  // eslint-disable-next-line no-unused-vars
  trackClickMail?: (data: Record<string, string>) => void,
  competitorName?: string
}>()

const trackingProps = computed(() => ({
  searchTerm: props.searchTerm,
  competitorName: props.competitorName || '',
  additionalInfo: props.additionalInfo || '',
  customerNo: userStore.companyNo || ''
}))

const emit = defineEmits<{
  close: []
}>()

const isLoading = ref(false)

const contactName = computed(() => {
  const data = salesPartnerData.value as any
  return data?.insideSalesPartnerName || data?.outsideSalesPartnerName || ''
})

const contactEmail = computed(() => {
  const data = salesPartnerData.value as any
  return data?.insideSalesPartnerEmail || data?.outsideSalesPartnerEmail || ''
})

const mailtoLink = ref('')

const buildMailtoLink = () => {
  // Return empty if no email to prevent malformed mailto links
  if (!contactEmail.value) {
    return '#'
  }

  // Return simple mailto link during SSR to avoid accessing user store getters
  if (process.server) {
    return `mailto:${contactEmail.value}`
  }

  try {
    const userName = userStore.name || ''
    const userEmail = userStore.email || ''
    const customerNo = userStore.companyNo || ''
    const currentDate = new Date().toLocaleDateString(i18nVue.locale.value)
    const companyName = (userStore.selectedCustomer as any)?.customerLabel || ''

    const variables = {
      contactName: contactName.value,
      searchTerm: props.searchTerm,
      additionalInfo: props.additionalInfo || '',
      currentDate,
      customerNo,
      userName,
      userEmail,
      companyName,
      competitorName: props.competitorName || ''
    }

    // Get subject and body from translations using i18n.tv
    let subject = ''
    let body = ''

    try {
      subject = i18nVue.t('ccrs.noMatchSubject')
    } catch {
      subject = ''
    }

    try {
      // Access raw translation message without compilation to avoid SyntaxError
      const messages = i18nVue.getLocaleMessage(i18nVue.locale.value) as any
      body = messages?.ccrs?.noMatchBody || ''

      // Replace all placeholders with actual values
      body = body.replaceAll('{{contactName}}', variables.contactName || '')
      body = body.replaceAll('{{searchTerm}}', variables.searchTerm || '')
      body = body.replaceAll('{{currentDate}}', variables.currentDate || '')
      body = body.replaceAll('{{customerNo}}', variables.customerNo || '')
      body = body.replaceAll('{{userName}}', variables.userName || '')
      body = body.replaceAll('{{companyName}}', variables.companyName || '')
      // add Addtional Info if exists - this is for the case when user is logged in and we want to pass more detailed info about the search
      if (variables.additionalInfo) {
        body = body.replaceAll('{{userEmail}}', `${variables.userEmail}\n\n${variables.additionalInfo}`)
      } else {
        body = body.replaceAll('{{userEmail}}', variables.userEmail || '')
      }
      if (variables.competitorName) {
        body = body.replaceAll('{{competitorName}}', variables.competitorName)
      }

      // Remove lines that still contain unreplaced placeholders
      body = body.split('\n').filter((line: string) => !line.match(/\{\{?\w+\}\}?/)).join('\n')

      // Clean up multiple consecutive newlines
      body = body.replace(/\n{3,}/g, '\n\n')
    } catch {
      body = ''
    }
    if (!subject || !body) {
      return `mailto:${contactEmail.value}`
    }

    // Add environment prefix for DEV/STG
    const finalSubject = (config.public.environment === 'DEV' || config.public.environment === 'STG')
      ? `[${config.public.environment}] ${subject}`
      : subject

    return `mailto:${contactEmail.value}?subject=${encodeURIComponent(finalSubject)}&body=${encodeURIComponent(body)}`
  } catch {
    // If any error occurs, return simple mailto link
    return `mailto:${contactEmail.value}`
  }
}

// Initialize mailtoLink with simple version for SSR
mailtoLink.value = buildMailtoLink()

const openRegularContactForm = () => {
  closePopup()
  ;(app.$eventbus as any).emit('showContactPopup', {
    simpleMode: true,
    topicId: '1e53b8d6c40045a1b3eca1d70eb29350',
    prefilledMessage: i18n.tv('searchQueryLabel', 'ccrs') + ': ' + props.searchTerm + '\n--------\n',
    trackingCallback: props.trackingCallback
  })
}

const copyEmail = async () => {
  if (contactEmail.value) {
    try {
      await navigator.clipboard.writeText(contactEmail.value)
      ;(app.$toast as any).success(i18n.tv('emailCopiedToClipboard', 'ccrs'))
      // Track email copy action
      if (props.trackCopyMail) {
        props.trackCopyMail()
      }
    } catch {
      // Failed to copy email
    }
  }
}

const handleMailtoClick = () => {
  // Track email click action
  if (props.trackClickMail) {
    props.trackClickMail(trackingProps.value)
  }
}

const closePopup = () => {
  emit('close')
}

// Fetch sales partner data when popup opens
watch(() => props.isOpen, async (newVal) => {
  if (newVal) {
    // If user is not logged in, open regular contact form
    if (!(auth as any).loggedIn) {
      openRegularContactForm()
      return
    }

    // Try to fetch sales partner data
    if (!salesPartnerData.value) {
      isLoading.value = true
      try {
        await fetchSalesPartnerData(loadedCustomerUuid.value)

        if (!salesPartnerData.value) {
          openRegularContactForm()
        }
      } finally {
        isLoading.value = false
      }
    }
  }
})

// Watch for changes to rebuild mailto link
watch([contactEmail, () => props.searchTerm, () => props.competitorName, () => props.additionalInfo], () => {
  if (process.client) {
    mailtoLink.value = buildMailtoLink()
  }
})

// Fetch on mount if not already loaded
onMounted(async () => {
  // Rebuild mailto link on client side with full translations
  mailtoLink.value = buildMailtoLink()

  if (!salesPartnerData.value && loadedCustomerUuid.value && (auth as any).loggedIn) {
    await fetchSalesPartnerData(loadedCustomerUuid.value)
  }
})
</script>

<style lang="scss">
.ccrs-contact-popup-container {
  .popup__wrapper .popup__content {
    padding: 1.5rem 1rem 1rem;
  }
}

.ccrs-contact-popup {
  h4 {
    font-size: 1.125rem;
    margin-top: -.5rem;
    margin-bottom: 1rem;
  }

  h5 {
    font-size: 1rem;
    margin: 0 0 .5rem;
  }

  &__row {
    min-height: 2rem;
    display: flex;
    align-items: center;
    margin-bottom: .125rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__label {
    display: inline-block;
    width: 6rem;
    margin-right: .5rem;
    font-weight: $base-text-font-weight-light;
  }

  &__copy {
    display: inline-flex;
    align-items: center;
    padding-right: 0.5rem;
    margin-right: 0.5rem;
    border-right: 0.125rem solid $color--input__border;
    text-decoration: none;
    cursor: pointer;

    svg {
      font-size: 1.5rem;
      color: $color__primary !important;
    }
  }

  &__email {
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    font-weight: $base-text-font-weight-semi-bold;

    svg {
      font-size: 1.5rem;
      color: $color__primary;
      margin-right: 0.25rem;
    }
  }

  a[href^="mailto:"] {
    svg {
      font-size: 1.5rem;
      color: $color__primary;
    }
  }
}
</style>
